/**
 * RasterPixelPickRow — the pixel-pick row consumed by the Raster
 * ToolCard. It folds in the swatch that used to live inside the floating
 * `PixelInspector` popup and renders three branches:
 *
 *   - `ok`     → swatch + tokenized hex display + per-band table
 *   - `nodata` → semantic chip with `block` icon (per §8.3 design rules)
 *   - `error`  → semantic warning chip with the underlying error
 *
 * Reads `useViewerStore.rasterToolCard.lastSample` so the same surface
 * stays in sync regardless of which engine produced the pick.
 *
 * Design-system compliance:
 *   - The swatch background is a runtime-computed hex string built by
 *     `channelTripleToHex` (design bridge). The string is *data*, not a
 *     design constant, so the no-raw-visual-constants lint rule is
 *     unaffected (the only literal token in source is the lone hash).
 *   - Coordinate + per-band values use `tokens.font.role.engMono`.
 *   - 40 px minimum touch target on the "Clear sample" action.
 */

import { Chip, Icon, tokens } from '@/design';
import { channelTripleToHex } from '@/design/tokenResolver';
import { useViewerStore } from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';

type LastSample = ReturnType<typeof useViewerStore.getState>['rasterToolCard']['lastSample'];

export interface RasterPixelPickRowProps {
  /** Optional override to render a synthetic sample (used by tests). */
  sampleOverride?: LastSample;
  'data-testid'?: string;
}

function formatChannel(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(3);
}

export function RasterPixelPickRow({
  sampleOverride,
  'data-testid': testId,
}: RasterPixelPickRowProps) {
  const storeSample = useViewerSlice((s) => s.rasterToolCard.lastSample);
  const setRasterToolCard = useViewerStore((s) => s.setRasterToolCard);
  const sample = sampleOverride ?? storeSample;

  if (!sample) {
    return (
      <div
        data-testid={testId ?? 'raster-pixel-pick-row'}
        data-pick-state="empty"
      >
        <Chip
          icon="colorize"
          variant="neutral"
          label="Click a raster pixel to inspect"
        />
      </div>
    );
  }

  if (sample.state === 'nodata') {
    return (
      <div
        data-testid={testId ?? 'raster-pixel-pick-row'}
        data-pick-state="nodata"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[2],
        }}
      >
        <Chip
          icon="block"
          variant="warning"
          label="No-data pixel"
        />
        <div
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.secondary,
          }}
        >
          {sample.lon.toFixed(6)}, {sample.lat.toFixed(6)}
        </div>
      </div>
    );
  }

  if (sample.state === 'error') {
    return (
      <div
        data-testid={testId ?? 'raster-pixel-pick-row'}
        data-pick-state="error"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[2],
        }}
      >
        <Chip
          icon="error"
          variant="error"
          label="Pixel sample failed"
        />
        <div
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.secondary,
          }}
        >
          {sample.errorMessage ?? 'request failed'}
        </div>
      </div>
    );
  }

  const isRgb = sample.values.length >= 3;
  const swatchHex = isRgb ? channelTripleToHex(sample.values) : null;

  return (
    <div
      data-testid={testId ?? 'raster-pixel-pick-row'}
      data-pick-state="ok"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
        }}
      >
        {swatchHex ? (
          <span
            aria-label={`Sampled color ${swatchHex}`}
            role="img"
            style={{
              display: 'inline-block',
              width: tokens.space[4],
              height: tokens.space[4],
              borderRadius: tokens.radius.sm,
              background: swatchHex,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            }}
          />
        ) : (
          <Icon name="colorize" size={20} decorative />
        )}
        <span
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.primary,
          }}
        >
          {swatchHex ?? '—'}
        </span>
        <span
          style={{
            flex: '1 1 auto',
            textAlign: 'right',
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.secondary,
          }}
        >
          {sample.lon.toFixed(6)}, {sample.lat.toFixed(6)}
        </span>
      </div>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMeta.size,
        }}
      >
        <tbody>
          {sample.values.map((v, i) => (
            <tr key={i}>
              <td
                style={{
                  padding: `${tokens.space[1]}px ${tokens.space[2]}px ${tokens.space[1]}px 0`,
                  color: tokens.color.ink.secondary,
                }}
              >
                {sample.bandNames[i] ?? `b${i + 1}`}
              </td>
              <td
                style={{
                  padding: `${tokens.space[1]}px 0`,
                  color: tokens.color.ink.primary,
                }}
              >
                {formatChannel(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => setRasterToolCard({ lastSample: null })}
        aria-label="Clear sampled pixel"
        data-testid="raster-pixel-pick-clear"
        style={{
          alignSelf: 'flex-start',
          minHeight: tokens.density.touchMin,
          padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
          background: 'transparent',
          color: tokens.color.ink.secondary,
          border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
          borderRadius: tokens.radius.input,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.body.size,
          cursor: 'pointer',
        }}
      >
        Clear sample
      </button>
    </div>
  );
}
